import { getUserInfo } from './auth';

/**
 * 资源码 localStorage 键：登录成功后由 LoginPage 调用 GraphQL currentUserResourceCodes，
 * 并用 saveUserResourceCodes(account.id, codes) 写入。
 */
const getResourceCodesKey = (userId: string) => `swiftx_user_resource_codes_${userId}`;

let cachedSet: Set<string> | null = null;
let cachedKey: string | null = null;

function clearResourceCodesCache(): void {
  cachedSet = null;
  cachedKey = null;
}

/** 写入当前用户的权限资源码列表（与后端 currentUserResourceCodes 一致） */
export function saveUserResourceCodes(userId: string, codes: string[]): void {
  localStorage.setItem(getResourceCodesKey(userId), JSON.stringify(codes));
  clearResourceCodesCache();
}

function getResourceCodesKeyForCurrentUser(): string | null {
  const userInfo = getUserInfo();
  const id = userInfo?.account?.id;
  return id != null && String(id) !== '' ? String(id) : null;
}

export function getUserResourceCodes(): string[] | null {
  const key = getResourceCodesKeyForCurrentUser();
  if (key == null) return null;
  try {
    const raw = localStorage.getItem(getResourceCodesKey(key));
    if (raw == null) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : null;
  } catch {
    return null;
  }
}

/**
 * 返回当前用户资源码的 Set（用于 O(1) 校验），带内存缓存
 */
function getResourceCodesSet(): Set<string> | null {
  const key = getResourceCodesKeyForCurrentUser();
  if (key == null) return null;
  if (cachedKey === key && cachedSet != null) return cachedSet;
  const arr = getUserResourceCodes();
  if (!arr?.length) return null;
  cachedSet = new Set(arr);
  cachedKey = key;
  return cachedSet;
}

export function removeUserResourceCodes(): void {
  const key = getResourceCodesKeyForCurrentUser();
  if (key != null) localStorage.removeItem(getResourceCodesKey(key));
  clearResourceCodesCache();
}

/**
 * 本地开发模式：放行全部资源码，免去本地补权限数据。
 * 仅在 `npm start`（NODE_ENV=development）且显式开启 REACT_APP_LOCAL_ALL_RESOURCES 时生效，
 * 生产构建为 production，该分支会被静态消除，不会带进线上产物。
 */
const LOCAL_ALL_RESOURCES =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_LOCAL_ALL_RESOURCES === 'true';

/**
 * 用户拥有 resourceCodes 中任意一个即返回 true（OR）；否则 false（内部用 Set 做 O(1) 查找）
 */
export function hasResource(resourceCodes: string[]): boolean {
  if (LOCAL_ALL_RESOURCES) return true;
  const set = getResourceCodesSet();
  if (!set?.size || !resourceCodes.length) return false;
  return resourceCodes.some(code => set.has(code));
}
